import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  TrendingUp,
  Eye,
  Heart,
  MessageSquare,
  Repeat2,
  Share2,
  Bookmark,
  Clock,
  MapPin,
  Sparkles,
  BarChart3,
  Zap,
  Target,
  ChevronRight,
  Flame,
  ArrowUp,
  ArrowDown,
  Users,
  Send,
  Loader2,
  RefreshCw,
} from 'lucide-react';

const SCORE_COLORS = {
  'Going Viral': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', ring: 'ring-red-500/40' },
  'On Fire': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', ring: 'ring-orange-500/40' },
  'Trending': { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', ring: 'ring-amber-500/40' },
  'Gaining Traction': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30', ring: 'ring-cyan-500/40' },
  'Building Momentum': { bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/30', ring: 'ring-violet-500/40' },
};

function ScoreBadge({ score, label }) {
  const colors = SCORE_COLORS[label] || { bg: 'bg-muted/40', text: 'text-muted-foreground', border: 'border-border/30' };
  return (
    <span data-testid="viral-score-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${colors.bg} ${colors.text} ${colors.border} border`}>
      <Flame className="w-3 h-3" />
      {score}% {label && `- ${label}`}
    </span>
  );
}

function ScoreRing({ score, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 90 ? '#EF4444' : score >= 75 ? '#F97316' : score >= 60 ? '#F59E0B' : score >= 40 ? '#06B6D4' : score >= 20 ? '#8B5CF6' : '#71717A';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{score}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Score</span>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, change }) {
  return (
    <div data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`} className="bg-muted/30 backdrop-blur-md border border-border rounded-xl p-4 hover:bg-muted/40 transition-all duration-300">
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {change !== undefined && (
          <span className={`text-xs font-medium flex items-center gap-0.5 ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {change >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(change)}%
          </span>
        )}
      </div>
      <p className="text-xl font-bold text-foreground">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

function SuggestionCard({ suggestion }) {
  const priorityColors = {
    high: 'border-l-red-500 bg-red-500/5',
    medium: 'border-l-amber-500 bg-amber-500/5',
    low: 'border-l-cyan-500 bg-cyan-500/5',
  };
  const typeIcons = { timing: Clock, boost: Zap, action: Target };
  const Icon = typeIcons[suggestion.type] || Sparkles;

  return (
    <div data-testid={`suggestion-${suggestion.id}`} className={`border-l-2 rounded-r-lg p-3 ${priorityColors[suggestion.priority] || priorityColors.medium}`}>
      <div className="flex items-start gap-2.5">
        <Icon className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{suggestion.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{suggestion.message}</p>
        </div>
      </div>
    </div>
  );
}

function TopPostRow({ post, rank }) {
  return (
    <div data-testid={`top-post-${rank}`} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors group">
      <span className="text-lg font-black text-muted-foreground/80 w-6 text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {rank}
      </span>
      {post.media_url ? (
        <img src={post.media_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
          <BarChart3 className="w-4 h-4 text-violet-400" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">{post.content || 'Untitled post'}</p>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" />{post.metrics?.views || 0}</span>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Heart className="w-3 h-3" />{post.metrics?.likes || 0}</span>
          <span className="text-[11px] text-muted-foreground flex items-center gap-1"><MessageSquare className="w-3 h-3" />{post.metrics?.comments || 0}</span>
        </div>
      </div>
      <ScoreBadge score={post.viral_score} label={post.viral_label} />
    </div>
  );
}

function AnalyzePanel({ token }) {
  const [postId, setPostId] = useState('');
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyze = async () => {
    if (!postId.trim()) { toast.error('Enter a post ID'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/viral/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: postId.trim(), question: question || undefined }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Analysis failed');
      setResult(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="analyze-panel" className="bg-muted/30 backdrop-blur-md border-border p-5">
      <h3 className="text-base font-bold text-foreground mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        <Sparkles className="w-4 h-4 text-amber-400" /> AI Post Analysis
      </h3>
      <div className="space-y-2">
        <input data-testid="analyze-post-id" value={postId} onChange={e => setPostId(e.target.value)}
          placeholder="Paste a post ID..." className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-violet-500/50" />
        <input data-testid="analyze-question" value={question} onChange={e => setQuestion(e.target.value)}
          placeholder="Ask a question (optional)..." className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-violet-500/50" />
        <Button data-testid="analyze-submit" onClick={analyze} disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 text-foreground text-sm rounded-full">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing...</> : <>Analyze Post</>}
        </Button>
      </div>
      {result && (
        <div data-testid="analyze-result" className="mt-4 space-y-3">
          <div className="flex items-center gap-3">
            <ScoreRing score={result.score?.viral_score || 0} size={64} />
            <div>
              <p className="text-sm font-semibold text-foreground">{result.score?.viral_label || 'Low Activity'}</p>
              <p className="text-xs text-muted-foreground">Engagement rate: {result.score?.engagement_rate || 0}%</p>
            </div>
          </div>
          {result.comparison && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(result.comparison).map(([k, v]) => (
                <span key={k} className={`text-[11px] px-2 py-0.5 rounded-full ${v === 'above' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {k}: {v === 'above' ? 'Above' : 'Below'} avg
                </span>
              ))}
            </div>
          )}
          <div className="bg-muted/20 rounded-lg p-3">
            <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">{result.analysis}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ViralEnginePage() {
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [trending, setTrending] = useState(null);
  const [geoTrending, setGeoTrending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [lbPeriod, setLbPeriod] = useState('week');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/viral/dashboard`, { headers });
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.warn('Dashboard fetch error:', e); }
  }, [token]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/viral/suggestions`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (e) { console.warn('Suggestions fetch error:', e); }
  }, [token]);

  const fetchTrending = useCallback(async () => {
    try {
      const res = await fetch(`${API}/viral/trending?limit=20`, { headers });
      if (res.ok) setTrending(await res.json());
    } catch (e) { console.warn('Trending fetch error:', e); }
  }, [token]);

  const fetchGeoTrending = useCallback(async () => {
    try {
      const res = await fetch(`${API}/viral/geo-trending`, { headers });
      if (res.ok) setGeoTrending(await res.json());
    } catch (e) { console.warn('Geo trending fetch error:', e); }
  }, [token]);

  const fetchLeaderboard = useCallback(async (period) => {
    try {
      const p = period || lbPeriod;
      const res = await fetch(`${API}/viral/leaderboard?period=${p}&limit=20`, { headers });
      if (res.ok) setLeaderboard(await res.json());
    } catch (e) { console.warn('Leaderboard fetch error:', e); }
  }, [token, lbPeriod]);

  const checkAlerts = async () => {
    setAlertsLoading(true);
    try {
      const res = await fetch(`${API}/viral/check-alerts`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        if (data.alerts?.length > 0) {
          toast.success(`${data.alerts.length} new viral alert(s)!`);
        } else {
          toast.info('No new viral alerts');
        }
      }
    } catch (e) { toast.error('Failed to check alerts'); }
    finally { setAlertsLoading(false); }
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    Promise.all([fetchDashboard(), fetchSuggestions(), fetchTrending(), fetchGeoTrending(), fetchLeaderboard()])
      .finally(() => setLoading(false));
  }, [token]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'trending', label: 'Trending', icon: TrendingUp },
    { id: 'leaderboard', label: 'Leaderboard', icon: Users },
    { id: 'geo', label: 'DMV Map', icon: MapPin },
    { id: 'analyze', label: 'AI Analysis', icon: Sparkles },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="viral-engine-page" className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                VIRAL ENGINE
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">Track, analyze, and amplify your content</p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="check-alerts-btn" onClick={checkAlerts} disabled={alertsLoading} variant="outline"
                className="border-border text-foreground/80 hover:text-foreground hover:bg-muted/30 text-xs rounded-full px-3 h-8">
                {alertsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                <span className="ml-1.5 hidden sm:inline">Check Alerts</span>
              </Button>
              <Button data-testid="refresh-btn" onClick={() => { fetchDashboard(); fetchSuggestions(); fetchTrending(); fetchGeoTrending(); }}
                variant="outline" className="border-border text-foreground/80 hover:text-foreground hover:bg-muted/30 text-xs rounded-full px-3 h-8">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-3 overflow-x-auto scrollbar-hide">
            {tabs.map(tab => (
              <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                  ${activeTab === tab.id ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'}`}>
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Score + Metrics Row */}
            <div className="flex flex-col md:flex-row gap-5">
              {/* Viral Score Ring */}
              <Card className="bg-muted/30 backdrop-blur-md border-border p-5 flex flex-col items-center justify-center md:w-48 shrink-0">
                <ScoreRing score={dashboard?.avg_viral_score || 0} />
                <p className="text-xs text-muted-foreground mt-2">Avg Viral Score</p>
                {dashboard?.avg_viral_score >= 20 && (
                  <ScoreBadge score={dashboard.avg_viral_score} label={
                    dashboard.avg_viral_score >= 90 ? 'Going Viral' :
                    dashboard.avg_viral_score >= 75 ? 'On Fire' :
                    dashboard.avg_viral_score >= 60 ? 'Trending' :
                    dashboard.avg_viral_score >= 40 ? 'Gaining Traction' : 'Building Momentum'
                  } />
                )}
              </Card>

              {/* Metric Grid */}
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <MetricCard icon={Eye} label="Total Views" value={dashboard?.total_views || 0} />
                <MetricCard icon={Heart} label="Engagements" value={dashboard?.total_engagement || 0} />
                <MetricCard icon={BarChart3} label="Engagement Rate" value={`${dashboard?.engagement_rate || 0}%`} />
                <MetricCard icon={Users} label="New Followers (7d)" value={dashboard?.follower_growth || 0} />
                <MetricCard icon={TrendingUp} label="Total Posts" value={dashboard?.total_posts || 0} />
                <MetricCard icon={Flame} label="Trending Posts" value={dashboard?.trending_posts?.length || 0} />
              </div>
            </div>

            {/* Best Posting Times */}
            {dashboard?.best_posting_times?.length > 0 && (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-5">
                <h3 className="text-base font-bold text-foreground mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <Clock className="w-4 h-4 text-cyan-400" /> Best Times to Post
                </h3>
                <div className="flex flex-wrap gap-2">
                  {dashboard.best_posting_times.map((t, i) => (
                    <div key={i} className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-4 py-2 text-center">
                      <p className="text-lg font-bold text-cyan-400">{t.display}</p>
                      <p className="text-[10px] text-muted-foreground">{t.avg_engagement} avg engagement</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Pre-Post Content Check */}
            <Card className="bg-muted/30 backdrop-blur-md border-border p-5" data-testid="pre-post-check">
              <h3 className="text-base font-bold text-foreground mb-1 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Sparkles className="w-4 h-4 text-violet-400" /> Before You Post
              </h3>
              <p className="text-xs text-muted-foreground/50 mb-3">Quick checks to maximize your reach</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-cyan-500/5 border border-cyan-500/10">
                  <Clock className="w-4 h-4 text-cyan-400 mb-1.5" />
                  <p className="text-xs font-semibold text-foreground">Best Time</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">{dashboard?.best_posting_times?.[0]?.display || 'Post at 8 PM for peak DMV engagement'}</p>
                </div>
                <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-500/10">
                  <Users className="w-4 h-4 text-violet-400 mb-1.5" />
                  <p className="text-xs font-semibold text-foreground">Audience Match</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">DMV creators are {dashboard?.total_posts > 5 ? 'active' : 'waiting'} for content like yours</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                  <Target className="w-4 h-4 text-amber-400 mb-1.5" />
                  <p className="text-xs font-semibold text-foreground">Pro Tips</p>
                  <p className="text-xs text-muted-foreground/60 mt-0.5">Add captions for +12% reach. Keep intro under 3 seconds.</p>
                </div>
              </div>
            </Card>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-5">
                <h3 className="text-base font-bold text-foreground mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <Target className="w-4 h-4 text-amber-400" /> Growth Suggestions
                </h3>
                <div className="space-y-2">
                  {suggestions.map((s, i) => <SuggestionCard key={i} suggestion={s} />)}
                </div>
              </Card>
            )}

            {/* Top Posts */}
            {dashboard?.top_posts?.length > 0 && (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-5">
                <h3 className="text-base font-bold text-foreground mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  <TrendingUp className="w-4 h-4 text-violet-400" /> Your Top Posts
                </h3>
                <div className="space-y-1">
                  {dashboard.top_posts.slice(0, 10).map((post, i) => (
                    <TopPostRow key={post.post_id || i} post={post} rank={i + 1} />
                  ))}
                </div>
              </Card>
            )}

            {/* Empty State */}
            {dashboard?.total_posts === 0 && (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-10 text-center">
                <BarChart3 className="w-12 h-12 text-muted-foreground/80 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-foreground mb-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>No Data Yet</h3>
                <p className="text-sm text-muted-foreground">Start posting content to see your viral analytics here.</p>
              </Card>
            )}
          </div>
        )}

        {/* Trending Tab */}
        {activeTab === 'trending' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Trending on DMV Soundstage
              </h2>
              <span className="text-xs text-muted-foreground">{trending?.total || 0} trending posts</span>
            </div>
            {trending?.trending?.length > 0 ? (
              <div className="space-y-2">
                {trending.trending.map((post, i) => (
                  <div key={post.post_id || i} data-testid={`trending-post-${i}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border hover:border-border transition-all">
                    <span className="text-xl font-black text-muted-foreground/80 w-8 text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {i + 1}
                    </span>
                    {post.media_url ? (
                      <img src={post.media_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-600/30 to-orange-500/20 flex items-center justify-center shrink-0">
                        <Flame className="w-5 h-5 text-orange-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground truncate">{post.content || 'Untitled'}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-muted-foreground">@{post.author_handle || post.author_name || 'unknown'}</span>
                        {post.location && (
                          <span className="text-[11px] text-muted-foreground/80 flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5" />{post.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <ScoreBadge score={post.viral_score} label={post.viral_label} />
                      <p className="text-[10px] text-muted-foreground mt-1">{post.engagement_rate}% eng.</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-10 text-center">
                <TrendingUp className="w-10 h-10 text-muted-foreground/80 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No trending posts this week. Keep creating!</p>
              </Card>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Viral Creator Leaderboard
              </h2>
              <div className="flex gap-1">
                {[{ v: 'week', l: 'Week' }, { v: 'month', l: 'Month' }, { v: 'all', l: 'All Time' }].map(p => (
                  <button key={p.v} data-testid={`lb-period-${p.v}`}
                    onClick={() => { setLbPeriod(p.v); fetchLeaderboard(p.v); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                      lbPeriod === p.v ? 'bg-violet-600 text-white' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    }`}>{p.l}</button>
                ))}
              </div>
            </div>
            {leaderboard?.leaderboard?.length > 0 ? (
              <div className="space-y-2">
                {leaderboard.leaderboard.map((c, i) => (
                  <div key={c.user_id} data-testid={`lb-rank-${c.rank}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                      i === 0 ? 'bg-amber-500/10 border-amber-500/20' :
                      i === 1 ? 'bg-muted/25 border-zinc-400/20' :
                      i === 2 ? 'bg-orange-700/10 border-orange-700/20' :
                      'bg-muted/20 border-border'
                    }`}>
                    <span className={`text-2xl font-black w-8 text-center ${
                      i === 0 ? 'text-amber-400' : i === 1 ? 'text-muted-foreground' : i === 2 ? 'text-orange-600' : 'text-muted-foreground/80'
                    }`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{c.rank}</span>
                    {c.avatar ? (
                      <img src={c.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-violet-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{c.name || c.handle || 'Creator'}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[11px] text-muted-foreground">{c.post_count} posts</span>
                        <span className="text-[11px] text-muted-foreground">{c.total_views.toLocaleString()} views</span>
                        <span className="text-[11px] text-muted-foreground">Best: {c.best_score}%</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-foreground">{Math.round(c.total_score)}</p>
                      <p className="text-[10px] text-muted-foreground">total score</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-10 text-center">
                <Users className="w-10 h-10 text-muted-foreground/80 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No leaderboard data for this period.</p>
              </Card>
            )}
          </div>
        )}

        {/* Geo Trending Tab */}
        {activeTab === 'geo' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Trending by DMV Region
            </h2>
            {geoTrending?.regions && Object.keys(geoTrending.regions).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(geoTrending.regions).map(([region, posts]) => (
                  <Card key={region} data-testid={`geo-region-${region.replace(/\s/g, '-')}`}
                    className="bg-muted/30 backdrop-blur-md border-border p-4 hover:border-border transition-all">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-orange-400" />
                      <h3 className="text-sm font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{region}</h3>
                      <span className="text-[10px] bg-muted/50 px-2 py-0.5 rounded-full text-muted-foreground">{posts.length} posts</span>
                    </div>
                    <div className="space-y-1.5">
                      {posts.map((post, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-foreground/80 truncate flex-1">{post.content || 'Untitled'}</span>
                          <span className="shrink-0">
                            <ScoreBadge score={post.viral_score} label={post.viral_label} />
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-muted/30 backdrop-blur-md border-border p-10 text-center">
                <MapPin className="w-10 h-10 text-muted-foreground/80 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No geo-trending data available yet.</p>
              </Card>
            )}
          </div>
        )}

        {/* AI Analysis Tab */}
        {activeTab === 'analyze' && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <h2 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              AI-Powered Post Analysis
            </h2>
            <p className="text-sm text-muted-foreground">Paste a post ID and get AI-driven insights on how to improve its performance.</p>
            <AnalyzePanel token={token} />
          </div>
        )}
      </div>
    </div>
  );
}
